import React, { useState, useEffect } from 'react';
import { calendarService } from '../services/api';
import {
  CalendarIcon,
  ScheduleIcon,
  BlockScheduleIcon,
  AddIcon,
  PlusIcon,
  TrashIcon,
  InfoCircleIcon,
  WarningIcon
} from '../assets/icons';
import ScheduleDialog from '../components/ScheduleDialog';
import ScheduleList from '../components/ScheduleList';
import BlockScheduleDialog from '../components/BlockScheduleDialog';
import BlockScheduleList from '../components/BlockScheduleList';
import DoctorSchedulesJsonPanel from '../components/DoctorSchedulesJsonPanel';
import { useLanguage } from '../contexts/LanguageContext';
import dayjs from 'dayjs';

const Calendar = () => {
  const { t } = useLanguage();
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [displayedAppointments, setDisplayedAppointments] = useState([]);
  const [newAppointment, setNewAppointment] = useState({
    customer_name: '',
    appointment_date: selectedDate.toISOString().split('T')[0],
    appointment_start_time: '',
    duration_minutes: 30,
    doctor_id: null,
    doctor_name: '',
    remarks: ''
  });
  const [showForm, setShowForm] = useState(false);
  const [editingAppointment, setEditingAppointment] = useState(null);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [doctors, setDoctors] = useState([]);
  const [timeSlots, setTimeSlots] = useState([]);
  const [scheduledAppointments, setScheduledAppointments] = useState([]);
  const [showCalendarView, setShowCalendarView] = useState(true);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [doctorSchedules, setDoctorSchedules] = useState({});
  const [scheduleLoading, setScheduleLoading] = useState(false);
  // True only after a successful schedules-by-date fetch for the current doctors list
  const [schedulesReady, setSchedulesReady] = useState(false);
  const [showScheduleForm, setShowScheduleForm] = useState(false);
  const [editingSchedule, setEditingSchedule] = useState(null);
  const [newSchedule, setNewSchedule] = useState({
    doctor_id: null,
    from_date: '',
    to_date: '',
    schedule: {
      "0": [{ from: "08:00", to: "18:00" }], // Monday
      "1": [{ from: "08:00", to: "18:00" }], // Tuesday
      "2": [{ from: "08:00", to: "18:00" }], // Wednesday
      "3": [{ from: "08:00", to: "18:00" }], // Thursday
      "4": [{ from: "08:00", to: "18:00" }], // Friday
      "5": [{ from: "08:00", to: "18:00" }], // Saturday
      "6": [{ from: "08:00", to: "18:00" }], // Sunday
      "step": "00:15"
    },
    is_default_schedule: true
  });
  const [showScheduleManager, setShowScheduleManager] = useState(false);
  const [allSchedules, setAllSchedules] = useState([]);
  const [selectedDoctorForSchedules, setSelectedDoctorForSchedules] = useState(null);
  const [showBlockScheduleManager, setShowBlockScheduleManager] = useState(false);
  const [showBlockScheduleForm, setShowBlockScheduleForm] = useState(false);
  const [editingBlockSchedule, setEditingBlockSchedule] = useState(null);
  const [newBlockSchedule, setNewBlockSchedule] = useState({
    doctor_id: null,
    block_from: '',
    block_until: '',
    reason: '',
    active: true
  });
  const [allBlockSchedules, setAllBlockSchedules] = useState([]);
  const [blockedSchedules, setBlockedSchedules] = useState([]);
  const [blockedSchedulesLoading, setBlockedSchedulesLoading] = useState(false);

  const fetchDoctors = async () => {
    try {
      // Use the calendarService's getDoctors method
      const response = await calendarService.getDoctors();
      // Access the 'items' array from the response
      const doctorsList = response.items || [];

      // Transform doctor data to match the format expected by the component
      const formattedDoctors = doctorsList.map(doctor => ({
        id: doctor.id,
        name: doctor.primary_name,
        speciality: doctor.speciality,
        service_provided: doctor.service_provided
      }));

      // // Add a system entry at the beginning if not present
      // if (formattedDoctors.length > 0 && formattedDoctors[0].id !== 1) {
      //   formattedDoctors.unshift({ id: 1, name: '系統' });
      // }

      setDoctors(formattedDoctors);
    } catch (error) {
      console.error('Error fetching doctors:', error);
      // Fallback to default system entry
      setDoctors([{ id: 1, name: t('calendar.system') }]);
    }
  };

  const fetchAppointments = async (startDate, endDate) => {
    setLoading(true);
    try {
      // Format dates as YYYY-MM-DD for the API
      const formattedStartDate = dayjs(startDate).format('YYYY-MM-DD');
      const formattedEndDate = dayjs(endDate).format('YYYY-MM-DD');

      // Use the updated calendarService
      const response = await calendarService.getAppointments(formattedStartDate, formattedEndDate);

      // Access the 'items' array from the response
      const appointmentsData = response.items || [];

      // Transform API data to match the format expected by the calendar
      const formattedAppointments = appointmentsData.map(appointment => {
        // Based on ConversationStateAppointment structure
        const appointmentDate = appointment.appointment_date;
        const appointmentTime = appointment.appointment_start_time;

        // Format time from "HH:MM:SS" to "HH:MM"
        let time = '';
        if (typeof appointmentTime === 'string') {
          time = appointmentTime.substring(0, 5);
        }

        return {
          id: appointment.id,
          doctor_id: appointment.doctor_id || 1, // Default to system if not specified
          appointment_date: appointmentDate,
          appointment_start_time: time,
          contact_number: appointment.contact_number,
          customer_name: appointment.customer_name || 'Unnamed Appointment',
          appointment_end_time: appointment.appointment_end_time,
          remarks: appointment.remarks,
          duration_minutes: appointment.duration_minutes || 30,
          clinic_name: appointment.clinic_name,
          doctor_name: appointment.doctor_name,
          status: appointment.status || 'requested' // Default to requested if not specified
        };
      });

      setScheduledAppointments(formattedAppointments);
    } catch (error) {
      console.error('Error fetching appointments:', error);
      alert(t('calendar.loadAppointmentsFailed'));
    } finally {
      setLoading(false);
    }
  };

  const fetchDoctorSchedules = async (date) => {
    setScheduleLoading(true);
    setSchedulesReady(false);
    try {
      // Format date as YYYY-MM-DD for the API (local timezone via dayjs)
      const formattedDate = dayjs(date).format('YYYY-MM-DD');

      // Fetch schedules for all doctors for this date
      const response = await calendarService.getSchedulesByDate(formattedDate);
      const schedulesData = response.items || [];

      // Map by numeric doctor_id. Missing / empty schedule => not available that day.
      const schedulesByDoctor = {};
      doctors.forEach((doctor) => {
        const id = Number(doctor.id);
        if (!Number.isNaN(id)) {
          schedulesByDoctor[id] = { doctor_id: id, date: formattedDate, schedule: [], step: 15 };
        }
      });
      schedulesData.forEach((doctorSchedule) => {
        if (doctorSchedule?.doctor_id == null) return;
        const id = Number(doctorSchedule.doctor_id);
        if (Number.isNaN(id)) return;
        const slots = Array.isArray(doctorSchedule.schedule) ? doctorSchedule.schedule : [];
        schedulesByDoctor[id] = {
          ...doctorSchedule,
          doctor_id: id,
          schedule: slots,
        };
      });

      setDoctorSchedules(schedulesByDoctor);
      setSchedulesReady(true);
    } catch (error) {
      console.error('Error fetching doctor schedules:', error);
      // Fail closed: keep doctors unavailable rather than bookable
      const unavailable = {};
      doctors.forEach((doctor) => {
        const id = Number(doctor.id);
        if (!Number.isNaN(id)) {
          unavailable[id] = { doctor_id: id, schedule: [], step: 15 };
        }
      });
      setDoctorSchedules(unavailable);
      setSchedulesReady(true);
    } finally {
      setScheduleLoading(false);
    }
  };

  const fetchBlockedSchedules = async (date) => {
    try {
      setBlockedSchedulesLoading(true);
      // Clear existing blocked schedules first
      setBlockedSchedules([]);

      // Format date as YYYY-MM-DD for the API
      const formattedDate = dayjs(date).format('YYYY-MM-DD');
      console.log('Fetching blocked schedules for date:', formattedDate);

      // Fetch blocked schedules for this date
      const response = await calendarService.getBlockSchedulesByDate(formattedDate);
      const blockedSchedulesData = response.items || [];

      console.log('Fetched blocked schedules:', blockedSchedulesData);
      setBlockedSchedules(blockedSchedulesData);
    } catch (error) {
      console.error('Error fetching blocked schedules:', error);
      // Don't show alert to avoid disrupting the user experience
    } finally {
      setBlockedSchedulesLoading(false);
    }
  };

  useEffect(() => {
    // Generate time slots from 6:00 to 20:00 to include pre-opening and post-closing hours
    const slots = [];
    for (let hour = 6; hour < 20; hour++) {
      for (let minute = 0; minute < 60; minute += 15) {
        const formattedHour = hour.toString().padStart(2, '0');
        const formattedMinute = minute.toString().padStart(2, '0');
        slots.push(`${formattedHour}:${formattedMinute}`);
      }
    }
    setTimeSlots(slots);

    // Fetch doctors
    fetchDoctors();

    // Set initial new appointment date
    // setNewAppointment(prev => ({
    //   ...prev,
    //   appointment_date: selectedDate.toISOString().split('T')[0],
    //   doctor_id: 1,
    //   doctor_name: '系統' // Default to system
    // }));
  }, []);

  useEffect(() => {
    // Calculate date range for the current month
    const startOfMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1);
    const endOfMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 0);

    // Fetch appointments for the current month
    fetchAppointments(startOfMonth, endOfMonth);
  }, [currentMonth]);

  // New useEffect to fetch doctor schedules and blocked schedules when selected date changes
  useEffect(() => {
    console.log('useEffect triggered - selectedDate:', selectedDate, 'doctors.length:', doctors.length);
    if (selectedDate && doctors.length > 0) {
      console.log('Fetching schedules and blocked schedules for date:', selectedDate);
      fetchDoctorSchedules(selectedDate);
      fetchBlockedSchedules(selectedDate);
    }
  }, [selectedDate, doctors]);

  // Format date for display
  const formatDate = (date) => {
    const options = { year: 'numeric', month: '2-digit', day: '2-digit', weekday: 'short' };
    return date.toLocaleDateString('zh-TW', options);
  };

  // Check if a date is today
  const isToday = (date) => {
    const today = new Date();
    return date.getDate() === today.getDate() &&
      date.getMonth() === today.getMonth() &&
      date.getFullYear() === today.getFullYear();
  };

  // Generate days for the current month (headers are Mon–Sun; JS getDay() is Sun=0)
  const getDaysInMonth = (year, month) => {
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const sundayFirstIndex = new Date(year, month, 1).getDay(); // 0=Sun … 6=Sat
    const mondayFirstOffset = (sundayFirstIndex + 6) % 7; // 0=Mon … 6=Sun

    const days = [];

    // Empty cells before the 1st so columns align with Mon–Sun headers
    for (let i = 0; i < mondayFirstOffset; i++) {
      days.push(null);
    }

    for (let i = 1; i <= daysInMonth; i++) {
      days.push(new Date(year, month, i));
    }

    return days;
  };

  // Render an appointment cell
  const renderAppointmentCell = (time, doctorId) => {
    // Get appointments for this exact time slot
    const appointmentsForCell = displayedAppointments.filter(
      app => app.appointment_start_time === time && app.doctor_id === doctorId
    );

    // If there are no appointments, return null
    if (appointmentsForCell.length === 0) return null;

    // Otherwise display the appointments
    return (
      <div className="w-full h-full relative z-10">
        {appointmentsForCell.map((app) => {
          // Check if this is an out-of-hours appointment
          const isOutOfHours = (() => {
            const openingHours = getOpeningHours(selectedDate, app.doctor_id);
            if (!openingHours?.from || !openingHours?.to) {
              // No schedule for this date → treat existing appointment as out of hours
              return true;
            }
            const [hour] = app.appointment_start_time.split(':').map(Number);
            const [openingHour] = openingHours.from.split(':').map(Number);
            const [closingHour] = openingHours.to.split(':').map(Number);
            return hour < openingHour || hour >= closingHour;
          })();

          return (
            <div
              key={app.id}
              className={`text-xs p-1 mb-1 hover:opacity-90 hover:shadow-sm transition-all duration-150 ${app.status === 'cancelled'
                  ? 'bg-gray-400 text-white'
                  : app.status === 'confirmed'
                    ? 'bg-green-600 text-white'
                    : app.status === 'clinic_requested'
                      ? 'bg-purple-600 text-white'
                      : 'bg-gray-600 text-white'
                } ${isOutOfHours ? 'border-l-2 border-r-2 border-yellow-500' : ''}`}
              onClick={(e) => handleAppointmentClick(app, e)}
            >
              {isOutOfHours && <span className="text-yellow-300 mr-1">⚠️</span>}
              {app.customer_name}
              {app.status === 'requested' && (
                <div className="text-xs bg-yellow-500 text-white px-1 mx-1 rounded mt-1 inline-block">{t('calendar.pendingConfirmation')}</div>
              )}
              {app.status === 'clinic_requested' && (
                <div className="text-xs bg-purple-500 text-white px-1 mx-1 rounded mt-1 inline-block">{t('calendar.clinicRequest')}</div>
              )}
              {isOutOfHours && (
                <div className="text-xs bg-yellow-500 text-white px-1 mx-1 rounded mt-1 inline-block">{app.appointment_start_time}</div>
              )}
              {app.doctor_name && app.doctor_name !== doctors.find(d => d.id === app.doctor_id)?.name && (
                <div className="text-xs">{app.doctor_name}</div>
              )}
              {app.clinic_name && <div className="text-xs">{app.clinic_name}</div>}
              {app.remarks && <div className="text-xs mt-1 whitespace-pre-line">{app.remarks}</div>}
              {app.duration_minutes && app.duration_minutes !== 30 && <div className="text-xs">{app.duration_minutes}{t('calendar.minutes')}</div>}
            </div>
          );
        })}
      </div>
    );
  };

  // Filter appointments for the selected date
  const filterAppointmentsByDate = (date) => {
    if (!date || !scheduledAppointments.length) return [];

    const dateString = date.toISOString().split('T')[0];

    return scheduledAppointments.filter(app => {
      if (app.appointment_date instanceof Date) {
        return app.appointment_date.getDate() === date.getDate() &&
          app.appointment_date.getMonth() === date.getMonth() &&
          app.appointment_date.getFullYear() === date.getFullYear();
      } else {
        // If the date is a string, normalize it for comparison
        const appDate = new Date(app.appointment_date);
        return appDate.getDate() === date.getDate() &&
          appDate.getMonth() === date.getMonth() &&
          appDate.getFullYear() === date.getFullYear();
      }
    });
  };

  // Update displayed appointments when selected date or all appointments change
  useEffect(() => {
    const filteredAppointments = filterAppointmentsByDate(selectedDate);

    // Check if there are any appointments outside of operating hours (before 9:00 or after 17:00)
    const hasOutOfHoursAppointments = filteredAppointments.some(app => {
      const time = app.appointment_start_time;
      if (!time) return false;

      const [hour] = time.split(':').map(Number);
      return hour < 8 || hour >= 18;
    });

    // Log if we found any out-of-hours appointments
    if (hasOutOfHoursAppointments) {
      console.log("Found appointments outside of operating hours");
    }

    setDisplayedAppointments(filteredAppointments);
  }, [selectedDate, scheduledAppointments]);

  const handleDateChange = (date) => {
    console.log('Date changed to:', date);
    setSelectedDate(date);

    // Update new appointment date when selected date changes
    setNewAppointment(prev => ({
      ...prev,
      appointment_date: date.toISOString().split('T')[0]
    }));
  };

  const getDoctorDateSchedule = (doctorId) => {
    const id = Number(doctorId);
    if (Number.isNaN(id)) return null;
    // After load, missing doctor => treat as empty schedule (not available)
    if (Object.prototype.hasOwnProperty.call(doctorSchedules, id)) {
      return doctorSchedules[id];
    }
    if (schedulesReady) {
      return { doctor_id: id, schedule: [], step: 15 };
    }
    return null;
  };

  // True when the doctor has at least one working slot on the selected date.
  // "No schedule" for the day => false => every timeslot unavailable.
  const hasDoctorScheduleForDate = (doctorId) => {
    if (scheduleLoading || !schedulesReady) return false;
    const doctorSchedule = getDoctorDateSchedule(doctorId);
    const slots = doctorSchedule?.schedule;
    return Array.isArray(slots) && slots.length > 0;
  };

  // Get opening hours for a specific day (date-scoped schedule list uses from_time/to_time)
  const getOpeningHours = (date, doctorId) => {
    try {
      if (!hasDoctorScheduleForDate(doctorId)) {
        return null;
      }

      const doctorSchedule = getDoctorDateSchedule(doctorId);
      const firstSlot = doctorSchedule.schedule[0];
      return {
        from: firstSlot.from_time || firstSlot.from,
        to: firstSlot.to_time || firstSlot.to,
      };
    } catch (error) {
      console.error('Error getting opening hours:', error);
      return null;
    }
  };

  // Helper function to check if a time slot is within a doctor's schedule for the selected date.
  // Fail closed: no schedule for the date => every timeslot unavailable.
  const isTimeSlotInDoctorSchedule = (time, doctorId) => {
    if (!hasDoctorScheduleForDate(doctorId)) {
      return false;
    }

    const slots = getDoctorDateSchedule(doctorId).schedule;
    const [hour, minute] = time.split(':').map(Number);
    const timeInMinutes = hour * 60 + minute;

    for (const slot of slots) {
      const from = slot.from_time || slot.from;
      const to = slot.to_time || slot.to;
      if (!from || !to) continue;

      const [fromHour, fromMinute] = from.split(':').map(Number);
      const [toHour, toMinute] = to.split(':').map(Number);
      if ([fromHour, fromMinute, toHour, toMinute].some((n) => Number.isNaN(n))) continue;

      const fromTimeInMinutes = fromHour * 60 + fromMinute;
      const toTimeInMinutes = toHour * 60 + toMinute;

      if (timeInMinutes >= fromTimeInMinutes && timeInMinutes < toTimeInMinutes) {
        return true;
      }
    }

    return false;
  };

  // Helper function to check if a time slot is blocked
  const isTimeSlotBlocked = (time, doctorId) => {
    // If still loading, don't show as blocked yet
    if (blockedSchedulesLoading) {
      return { isBlocked: false };
    }

    if (blockedSchedules.length === 0) {
      return { isBlocked: false };
    }

    // Use local calendar date (avoid UTC shift from toISOString)
    const selectedDateStr = dayjs(selectedDate).format('YYYY-MM-DD');
    const selectedDateTime = dayjs(`${selectedDateStr} ${time}`);

    // Check each blocked schedule (clinic "Holiday" / leave blocks)
    for (const blockedSchedule of blockedSchedules) {
      // Skip inactive blocked schedules
      if (!blockedSchedule.active) {
        continue;
      }

      // Check if this blocked schedule affects this doctor
      // If doctor_id is null, it affects all doctors
      // If doctor_id matches, it affects this specific doctor
      if (
        blockedSchedule.doctor_id !== null &&
        blockedSchedule.doctor_id !== undefined &&
        Number(blockedSchedule.doctor_id) !== Number(doctorId)
      ) {
        continue;
      }

      const blockFrom = dayjs(blockedSchedule.block_from);
      const blockUntil = dayjs(blockedSchedule.block_until);

      // Inclusive range: block_from <= slot < block_until+1s equivalent via isSameOrAfter/isSameOrBefore
      if (
        (selectedDateTime.isAfter(blockFrom) || selectedDateTime.isSame(blockFrom)) &&
        (selectedDateTime.isBefore(blockUntil) || selectedDateTime.isSame(blockUntil))
      ) {
        return {
          isBlocked: true,
          reason: blockedSchedule.reason,
          blockSchedule: blockedSchedule
        };
      }
    }

    return { isBlocked: false };
  };

  // Add dummy function for cell click
  const handleCellClick = (time, doctorId) => {
    // No schedule for the date => reject every timeslot
    if (!hasDoctorScheduleForDate(doctorId) || !isTimeSlotInDoctorSchedule(time, doctorId)) {
      alert(t('calendar.doctorUnavailable'));
      return;
    }

    // Check if this time slot is blocked
    const blockedInfo = isTimeSlotBlocked(time, doctorId);
    if (blockedInfo.isBlocked) {
      alert(t('calendar.timeSlotBlocked', `This time slot is blocked: ${blockedInfo.reason}`));
      return;
    }

    // Set the new appointment details based on the clicked cell
    const selectedDoctor = doctors.find(doctor => doctor.id === doctorId);
    setNewAppointment({
      customer_name: '', // You can set a default title if needed
      appointment_date: selectedDate.toISOString().split('T')[0], // Set to the selected date
      appointment_start_time: time,
      duration_minutes: 30, // Default duration
      doctor_id: doctorId,
      doctor_name: selectedDoctor?.name || '',
      remarks: ''
    });
    setShowForm(true); // Show the appointment form
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    setNewAppointment(prev => {
      const updatedAppointment = {
        ...prev,
        [name]: value
      };

      // If the doctorId changed, update the doctorName accordingly
      if (name === 'doctor_id') {
        const selectedDoctor = doctors.find(doctor => doctor.id === parseInt(value));
        updatedAppointment.doctorName = selectedDoctor?.name || '';
      }

      return updatedAppointment;
    });
  };

  // Submit the form to create a new appointment
  const handleSubmit = async (e) => {
    e.preventDefault();
    setActionLoading(true);

    try {
      // Format the data for the API based on ConversationStateAppointment structure
      const appointmentData = {
        customer_name: newAppointment.customer_name,
        appointment_date: newAppointment.appointment_date,
        appointment_start_time: newAppointment.appointment_start_time,
        duration_minutes: parseInt(newAppointment.duration_minutes),
        doctor_id: parseInt(newAppointment.doctor_id) || 1,
        doctor_name: doctors.find(d => d.id === parseInt(newAppointment.doctor_id))?.name || '',
        remarks: newAppointment.remarks || ""
      };

      if (editingAppointment) {
        // We're editing an existing appointment
        // Call the API to update the appointment
        const response = await calendarService.updateAppointment(editingAppointment.id, appointmentData);

        // Update the appointment in the local state with the response data
        const updatedAppointment = {
          ...editingAppointment,
          doctor_id: response.doctor_id,
          appointment_date: response.appointment_date,
          appointment_start_time: response.appointment_start_time.substring(0, 5), // Extract HH:MM
          customer_name: response.customer_name,
          remarks: response.remarks,
          duration_minutes: response.duration_minutes,
          doctor_name: response.doctor_name,
          clinic_name: response.clinic_name,
          status: response.status || 'confirmed'
        };

        setScheduledAppointments(prev =>
          prev.map(app => app.id === editingAppointment.id ? updatedAppointment : app)
        );

        // Show success message
        alert(t('calendar.appointmentUpdatedConfirmed'));
      } else {
        // We're creating a new appointment
        // TODO: Uncomment this section when the backend API is implemented
        // const createdAppointment = await calendarService.createAppointment(appointmentData);

        // For now, just add it to the local state
        const newAppointmentObj = {
          id: Date.now().toString(),
          doctor_id: appointmentData.doctor_id,
          appointment_date: appointmentData.appointment_date,
          appointment_start_time: appointmentData.appointment_start_time,
          customer_name: appointmentData.customer_name,
          type: 'gray',
          status: 'requested',
          remarks: appointmentData.remarks,
          duration_minutes: appointmentData.duration_minutes,
          doctor_name: appointmentData.doctor_name,
          clinic_name: appointmentData.clinic_name || ''
        };

        setScheduledAppointments(prev => [...prev, newAppointmentObj]);
      }

      // Reset form and close it
      setNewAppointment({
        customer_name: '',
        appointment_date: selectedDate.toISOString().split('T')[0],
        appointment_start_time: '',
        status: 'requested',
        duration_minutes: 30,
        doctor_id: doctors[0].id,
        doctor_name: doctors[0].name,
        remarks: ''
      });

      setShowForm(false);
      setEditingAppointment(null);

      // Refresh appointments to show the new one
      const startOfMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1);
      const endOfMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 0);
      fetchAppointments(startOfMonth, endOfMonth);

    } catch (error) {
      console.error('Error saving appointment:', error);
      alert(t('calendar.saveAppointmentFailed'));
    } finally {
      setActionLoading(false);
    }
  };

  const toggleCalendarView = () => {
    setShowCalendarView(!showCalendarView);
  };

  // Navigation functions for month calendar
  const prevMonth = () => {
    const newMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1);
    setCurrentMonth(newMonth);
  };

  const nextMonth = () => {
    const newMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1);
    setCurrentMonth(newMonth);
  };

  const goToToday = () => {
    const today = new Date();
    setCurrentMonth(new Date(today.getFullYear(), today.getMonth(), 1));
    setSelectedDate(today);
  };

  // Get days for calendar view
  const days = getDaysInMonth(currentMonth.getFullYear(), currentMonth.getMonth());

  // Handle appointment click
  const handleAppointmentClick = (app, e) => {
    e.stopPropagation();
    setSelectedAppointment(app);
    setShowConfirmDialog(true);
  };

  // Confirm appointment without changing time
  const handleConfirmAppointment = async () => {
    if (!selectedAppointment) return;

    try {
      setActionLoading(true);

      // Call the API to update the appointment status
      const response = await calendarService.updateAppointmentStatus(selectedAppointment.id, 'confirmed');

      // Update the appointment in the local state with the response data
      const updatedAppointment = {
        ...selectedAppointment,
        status: response.status || 'confirmed',
        doctor_id: response.doctor_id,
        appointment_date: response.appointment_date,
        appointment_start_time: response.appointment_start_time.substring(0, 5), // Extract HH:MM
        customer_name: response.customer_name,
        remarks: response.remarks,
        duration_minutes: response.duration_minutes,
        doctor_name: response.doctor_name,
        clinic_name: response.clinic_name
      };

      // Update the appointment in the local state
      setScheduledAppointments(prev =>
        prev.map(app => app.id === selectedAppointment.id ? updatedAppointment : app)
      );

      // Close the dialog
      setShowConfirmDialog(false);
      setSelectedAppointment(null);

      // Show success message
      alert(t('calendar.appointmentConfirmed'));
    } catch (error) {
      console.error('Error confirming appointment:', error);
      alert(t('calendar.confirmAppointmentFailed'));
    } finally {
      setActionLoading(false);
    }
  };

  // Edit appointment time and confirm
  const handleEditAppointment = () => {
    if (!selectedAppointment) return;

    // Set the form data with the selected appointment details
    setNewAppointment({
      customer_name: selectedAppointment.customer_name,
      appointment_date: selectedAppointment.appointment_date,
      appointment_start_time: selectedAppointment.appointment_start_time,
      duration_minutes: selectedAppointment.duration_minutes || 30,
      doctor_id: selectedAppointment.doctor_id,
      doctor_name: selectedAppointment.doctor_name,
      remarks: selectedAppointment.remarks || ''
    });

    // Set editing mode
    setEditingAppointment(selectedAppointment);

    // Close the confirmation dialog and show the form
    setShowConfirmDialog(false);
    setShowForm(true);
  };

  // Cancel appointment
  const handleCancelAppointment = async () => {
    if (!selectedAppointment) return;

    try {
      setActionLoading(true);

      // Call the API to update the appointment status
      await calendarService.updateAppointmentStatus(selectedAppointment.id, 'cancelled');

      // Create a copy of the appointment with status set to cancelled
      const updatedAppointment = {
        ...selectedAppointment,
        status: 'cancelled'
      };

      // Update the appointment in the local state
      setScheduledAppointments(prev =>
        prev.map(app => app.id === selectedAppointment.id ? updatedAppointment : app)
      );

      // Close the dialog
      setShowConfirmDialog(false);
      setSelectedAppointment(null);

      // Show success message
      alert(t('calendar.appointmentCancelled'));
    } catch (error) {
      console.error('Error cancelling appointment:', error);
      alert(t('calendar.cancelAppointmentFailed'));
    } finally {
      setActionLoading(false);
    }
  };

  // Submit handler for schedule form
  const handleScheduleSubmit = async (e) => {
    e.preventDefault();
    try {
      setActionLoading(true);

      // Validate form
      if (!newSchedule.doctor_id) {
        alert(t('calendar.pleaseSelectDoctor'));
        return;
      }

      if (!newSchedule.is_default_schedule && (!newSchedule.from_date || !newSchedule.to_date)) {
        alert(t('calendar.pleaseSelectDateRange'));
        return;
      }

      // Ensure all days have proper schedule format
      const finalSchedule = { ...newSchedule.schedule };
      for (let i = 0; i < 7; i++) {
        if (!finalSchedule[i.toString()]) {
          finalSchedule[i.toString()] = [];
        }
      }

      const scheduleData = {
        doctor_id: parseInt(newSchedule.doctor_id),
        from_date: newSchedule.is_default_schedule ? null : newSchedule.from_date,
        to_date: newSchedule.is_default_schedule ? null : newSchedule.to_date,
        schedule: finalSchedule,
        is_default_schedule: newSchedule.is_default_schedule
      };

      if (editingSchedule) {
        // Update existing schedule
        await calendarService.updateSchedule(editingSchedule.id, scheduleData);
        alert(t('calendar.scheduleUpdated'));
      } else {
        // Create new schedule
        await calendarService.createSchedule(scheduleData);
        alert(t('calendar.scheduleCreated'));
      }

      // Refresh both views
      fetchDoctorSchedules(selectedDate);

      // Also refresh the schedule list if we're in the manager view
      if (showScheduleManager && selectedDoctorForSchedules) {
        fetchAllSchedulesForDoctor(selectedDoctorForSchedules.id);
      }

      // Close form
      setShowScheduleForm(false);
      setEditingSchedule(null);

    } catch (error) {
      console.error('Error saving schedule:', error);
      alert(t('calendar.saveScheduleFailed'));
    } finally {
      setActionLoading(false);
    }
  };

  // Handle schedule form changes
  const handleScheduleChange = (updatedSchedule) => {
    setNewSchedule(updatedSchedule);
  };

  // Reset and close schedule form
  const handleCloseScheduleForm = () => {
    setShowScheduleForm(false);
    setEditingSchedule(null);
    setNewSchedule({
      doctor_id: doctors.length > 0 ? doctors[0].id : null,
      from_date: '',
      to_date: '',
      schedule: {
        "0": [{ from: "08:00", to: "18:00" }], // Monday
        "1": [{ from: "08:00", to: "18:00" }], // Tuesday
        "2": [{ from: "08:00", to: "18:00" }], // Wednesday
        "3": [{ from: "08:00", to: "18:00" }], // Thursday
        "4": [{ from: "08:00", to: "18:00" }], // Friday
        "5": [{ from: "08:00", to: "18:00" }], // Saturday
        "6": [{ from: "08:00", to: "18:00" }], // Sunday
        "step": "00:15"
      },
      is_default_schedule: true
    });
  };

  // Fetch active schedules for a specific doctor
  const fetchAllSchedulesForDoctor = async (doctorId) => {
    try {
      setScheduleLoading(true);
      const response = await calendarService.getDoctorSchedules(doctorId);
      setAllSchedules(response.items || []);
    } catch (error) {
      console.error('Error fetching schedules:', error);
      alert(t('calendar.loadScheduleFailed'));
    } finally {
      setScheduleLoading(false);
    }
  };

  // Handle showing the schedule manager
  const handleShowScheduleManager = () => {
    setShowScheduleManager(true);
    if (doctors.length > 0) {
      const firstDoctor = doctors[0];
      setSelectedDoctorForSchedules(firstDoctor);
      fetchAllSchedulesForDoctor(firstDoctor.id);
    }
  };

  // Handle doctor change in schedule manager
  const handleScheduleManagerDoctorChange = (e) => {
    const doctorId = parseInt(e.target.value);
    const doctor = doctors.find(d => d.id === doctorId);
    setSelectedDoctorForSchedules(doctor);
    fetchAllSchedulesForDoctor(doctorId);
  };

  // Handle edit schedule in the list
  const handleEditSchedule = (schedule) => {
    setEditingSchedule(schedule);
    setNewSchedule({
      ...schedule,
      doctor_id: schedule.doctor_id
    });
    setShowScheduleForm(true);
  };

  // Handle delete schedule
  const handleDeleteSchedule = async (schedule) => {
    if (!window.confirm(t('calendar.confirmDeleteSchedule'))) {
      return;
    }

    try {
      setActionLoading(true);
      await calendarService.deleteSchedule(schedule.id);
      alert(t('calendar.scheduleDeleted'));

      // Refresh schedules
      fetchAllSchedulesForDoctor(selectedDoctorForSchedules.id);
      fetchDoctorSchedules(selectedDate);
    } catch (error) {
      console.error('Error deleting schedule:', error);
      alert(t('calendar.deleteScheduleFailed'));
    } finally {
      setActionLoading(false);
    }
  };

  // Handle adding a new schedule from the manager
  const handleAddScheduleFromManager = () => {
    setNewSchedule({
      doctor_id: selectedDoctorForSchedules?.id || (doctors.length > 0 ? doctors[0].id : null),
      from_date: '',
      to_date: '',
      schedule: {
        "0": [{ from: "08:00", to: "18:00" }], // Monday
        "1": [{ from: "08:00", to: "18:00" }], // Tuesday
        "2": [{ from: "08:00", to: "18:00" }], // Wednesday
        "3": [{ from: "08:00", to: "18:00" }], // Thursday
        "4": [{ from: "08:00", to: "18:00" }], // Friday
        "5": [{ from: "08:00", to: "18:00" }], // Saturday
        "6": [{ from: "08:00", to: "18:00" }], // Sunday
        "step": "00:15"
      },
      is_default_schedule: true
    });
    setEditingSchedule(null);
    setShowScheduleForm(true);
  };

  // Block Schedule Management Functions
  const fetchAllBlockSchedules = async () => {
    try {
      setScheduleLoading(true);
      const response = await calendarService.getAllBlockSchedules();
      setAllBlockSchedules(response.items || []);
    } catch (error) {
      console.error('Error fetching block schedules:', error);
      alert(t('calendar.loadBlockScheduleFailed'));
    } finally {
      setScheduleLoading(false);
    }
  };

  const handleShowBlockScheduleManager = () => {
    setShowBlockScheduleManager(true);
    fetchAllBlockSchedules();
  };


  const handleEditBlockSchedule = (blockSchedule) => {
    setEditingBlockSchedule(blockSchedule);
    setNewBlockSchedule({
      ...blockSchedule,
      doctor_id: blockSchedule.doctor_id
    });
    setShowBlockScheduleForm(true);
  };

  const handleDeleteBlockSchedule = async (blockSchedule) => {
    if (!window.confirm(t('calendar.confirmDeleteBlockSchedule'))) {
      return;
    }

    try {
      setActionLoading(true);
      await calendarService.deleteBlockSchedule(blockSchedule.id);
      alert(t('calendar.blockScheduleDeleted'));

      // Refresh block schedules
      fetchAllBlockSchedules();

      // Refresh blocked schedules for the current date
      fetchBlockedSchedules(selectedDate);
    } catch (error) {
      console.error('Error deleting block schedule:', error);
      alert(t('calendar.deleteBlockScheduleFailed'));
    } finally {
      setActionLoading(false);
    }
  };

  const handleAddBlockScheduleFromManager = () => {
    setNewBlockSchedule({
      doctor_id: null,
      block_from: '',
      block_until: '',
      reason: '',
      active: true
    });
    setEditingBlockSchedule(null);
    setShowBlockScheduleForm(true);
  };

  const handleBlockScheduleSubmit = async (e) => {
    e.preventDefault();
    try {
      setActionLoading(true);

      // Validate form
      if (!newBlockSchedule.reason) {
        alert(t('calendar.pleaseEnterReason'));
        return;
      }

      if (!newBlockSchedule.block_from || !newBlockSchedule.block_until) {
        alert(t('calendar.pleaseSelectDateTimeRange'));
        return;
      }

      // Convert date-only (YYYY-MM-DD) to full day range for API
      let blockFrom = newBlockSchedule.block_from;
      let blockUntil = newBlockSchedule.block_until;
      if (String(blockFrom).length === 10) blockFrom = `${blockFrom}T00:00:00`;
      if (String(blockUntil).length === 10) blockUntil = `${blockUntil}T23:59:59`;

      const blockScheduleData = {
        doctor_id: newBlockSchedule.doctor_id,
        block_from: blockFrom,
        block_until: blockUntil,
        reason: newBlockSchedule.reason,
        active: newBlockSchedule.active
      };

      if (editingBlockSchedule) {
        // Update existing block schedule
        await calendarService.updateBlockSchedule(editingBlockSchedule.id, blockScheduleData);
        alert(t('calendar.blockScheduleUpdated'));
      } else {
        // Create new block schedule
        await calendarService.createBlockSchedule(blockScheduleData);
        alert(t('calendar.blockScheduleCreated'));
      }

      // Refresh block schedules
      if (showBlockScheduleManager) {
        fetchAllBlockSchedules();
      }

      // Refresh blocked schedules for the current date
      fetchBlockedSchedules(selectedDate);

      // Close form
      setShowBlockScheduleForm(false);
      setEditingBlockSchedule(null);

    } catch (error) {
      console.error('Error saving block schedule:', error);
      alert(t('calendar.saveBlockScheduleFailed'));
    } finally {
      setActionLoading(false);
    }
  };

  const handleBlockScheduleChange = (updatedBlockSchedule) => {
    setNewBlockSchedule(updatedBlockSchedule);
  };

  const handleCloseBlockScheduleForm = () => {
    setShowBlockScheduleForm(false);
    setEditingBlockSchedule(null);
    setNewBlockSchedule({
      doctor_id: null,
      block_from: '',
      block_until: '',
      reason: '',
      active: true
    });
  };

  return (
    <div className="bg-gray-50 min-h-screen overflow-hidden flex">
      {/* Left side - Calendar selector */}
      {showCalendarView && (
        <div className="w-64 bg-white border-r p-4 overflow-y-auto">
          <div className="mb-4">
            <div className="flex justify-between items-center mb-4">
              <h2 className="font-medium text-gray-700">
                {currentMonth.toLocaleString('zh-TW', { month: 'long', year: 'numeric' })}
              </h2>
              <div className="flex space-x-1">
                <button onClick={prevMonth} className="p-1 text-gray-600 hover:bg-gray-200 hover:text-gray-900 rounded transition-colors duration-150">
                  &lt;
                </button>
                <button onClick={goToToday} className="p-1 text-gray-600 hover:bg-blue-100 hover:text-blue-700 rounded text-xs transition-colors duration-150">
                  {t('calendar.today')}
                </button>
                <button onClick={nextMonth} className="p-1 text-gray-600 hover:bg-gray-200 hover:text-gray-900 rounded transition-colors duration-150">
                  &gt;
                </button>
              </div>
            </div>

            <div className="grid grid-cols-7 gap-1 text-center">
              {t('calendar.days').map(day => (
                <div key={day} className="text-xs font-medium text-gray-500 py-1">
                  {day}
                </div>
              ))}

              {days.map((day, index) => {
                if (!day) return <div key={`empty-${index}`} className="py-2"></div>;

                const isSelected = day.getDate() === selectedDate.getDate() &&
                  day.getMonth() === selectedDate.getMonth() &&
                  day.getFullYear() === selectedDate.getFullYear();

                const isTodayDate = isToday(day);

                // Check if there are appointments for this day
                const dayStr = day.toISOString().split('T')[0];
                const hasAppointments = scheduledAppointments.some(app => {
                  // Check if the date matches - it could be a Date object or a string
                  if (app.appointment_date instanceof Date) {
                    return app.appointment_date.getDate() === day.getDate() &&
                      app.appointment_date.getMonth() === day.getMonth() &&
                      app.appointment_date.getFullYear() === day.getFullYear();
                  } else {
                    // Handle string date format 'YYYY-MM-DD'
                    const appDate = new Date(app.appointment_date);
                    return appDate.getDate() === day.getDate() &&
                      appDate.getMonth() === day.getMonth() &&
                      appDate.getFullYear() === day.getFullYear();
                  }
                });

                return (
                  <button
                    key={`day-${day.getDate()}`}
                    onClick={() => handleDateChange(day)}
                    className={`py-2 text-sm relative hover:bg-blue-100 transition-colors duration-150 ${isSelected
                      ? 'bg-blue-500 text-white rounded-full font-bold hover:bg-blue-600'
                      : isTodayDate
                        ? 'text-blue-600 font-semibold'
                        : 'text-gray-700 hover:bg-gray-100'
                      }`}
                  >
                    {day.getDate()}
                    {hasAppointments && (
                      <span className={`absolute bottom-0 left-1/2 transform -translate-x-1/2 w-1 h-1 rounded-full ${isSelected ? 'bg-white' : 'bg-blue-500'
                        }`}></span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="border-t pt-4">
            <h3 className="font-medium text-gray-700 mb-2">{t('calendar.search')}</h3>
            <input
              type="text"
              placeholder={t('calendar.searchAppointments')}
              className="w-full p-2 border rounded text-sm"
            />
          </div>

          <div className="border-t pt-4 mt-4">
            <h3 className="font-medium text-gray-700 mb-2">{t('calendar.doctors')}</h3>
            <div className="space-y-2">
              {doctors.slice(0, 5).map(doctor => (
                <div key={doctor.id} className="flex items-center hover:bg-gray-50 p-1 rounded transition-colors duration-150">
                  <input
                    type="checkbox"
                    id={`doctor-${doctor.id}`}
                    defaultChecked
                    className="mr-2"
                  />
                  <label htmlFor={`doctor-${doctor.id}`} className="text-sm text-gray-700 cursor-pointer hover:text-gray-900">
                    {doctor.name}
                  </label>
                </div>
              ))}
              {doctors.length > 5 && (
                <button className="text-blue-500 text-sm hover:text-blue-700 hover:underline transition-colors duration-150">{t('calendar.viewMore')}</button>
              )}
            </div>
          </div>
        </div>
      )}
      {/* Right side - Main content */}
      <div className={`flex-1 ${showCalendarView ? 'w-[calc(100%-16rem)]' : 'w-full'}`}>
        {/* Top date indicator with toggle button */}
        <div className="sticky top-0 z-10 bg-gray-100 border-b px-4 py-2 flex justify-between items-center">
          <div className="flex items-center gap-2">
            <button
              onClick={toggleCalendarView}
              className="text-gray-600 hover:text-gray-900 hover:bg-gray-200 p-2 rounded transition-colors duration-150"
              title={showCalendarView ? t('calendar.hideCalendar') : t('calendar.showCalendar')}
            >
              <CalendarIcon />
            </button>
            <button
              onClick={handleShowScheduleManager}
              className="text-gray-600 hover:text-gray-900 hover:bg-gray-200 p-2 rounded transition-colors duration-150"
              title={t('calendar.manageSchedule')}
            >
              <ScheduleIcon />
            </button>
            <button
              onClick={handleShowBlockScheduleManager}
              className="text-gray-600 hover:text-gray-900 hover:bg-gray-200 p-2 rounded transition-colors duration-150"
              title={t('calendar.manageBlockSchedule')}
            >
              <BlockScheduleIcon />
            </button>
          </div>
          <div className="text-sm text-center">
            <div className="font-semibold">
              {formatDate(selectedDate)}
              {isToday(selectedDate) && <span className="ml-2 text-blue-500">· {t('calendar.today')}</span>}
            </div>
            <div className="text-xs text-gray-500">
              {displayedAppointments.length > 0
                ? `${displayedAppointments.length} ${t('calendar.appointments')}`
                : t('calendar.noAppointments')}
            </div>
          </div>
          {/* <button
            onClick={() => setShowForm(true)}
            className="text-blue-500 hover:text-blue-700 hover:bg-blue-50 p-2 rounded transition-colors duration-150"
            title={t('calendar.addAppointment')}
          >
            <AddIcon />
          </button> */}
        </div>

        {/* Calendar table */}
        <div className="w-full overflow-x-auto">
          {/* Daily summary */}
          {displayedAppointments.length > 0 && (
            <div className="bg-white p-4 border-b">
              <h3 className="text-sm font-semibold mb-2">
                {formatDate(selectedDate)} {t('calendar.appointmentRequestSummary')}
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {displayedAppointments.filter(app => app.status === 'requested').slice(0, 4).map((app) => (
                  <div
                    key={app.id}
                    className={`border rounded p-2 text-xs hover:shadow-md hover:border-blue-300 transition-all duration-150 cursor-pointer ${app.status === 'cancelled' ? 'bg-gray-100' :
                        app.status === 'confirmed' ? 'bg-green-50 border-green-200' :
                          app.status === 'clinic_requested' ? 'bg-purple-50 border-purple-200' : ''
                      }`}
                    onClick={(e) => handleAppointmentClick(app, e)}
                  >
                    <div className="font-medium">
                      {app.appointment_start_time} - {app.customer_name}
                      {/* {JSON.stringify(app)} */}
                      {app.status === 'requested' && (
                        <span className="ml-2 text-xs bg-yellow-500 text-white px-1 rounded">{t('calendar.pendingConfirmation')}</span>
                      )}
                      {app.status === 'confirmed' && (
                        <span className="ml-2 text-xs bg-green-500 text-white px-1 rounded">{t('calendar.confirmed')}</span>
                      )}
                      {app.status === 'cancelled' && (
                        <span className="ml-2 text-xs bg-gray-500 text-white px-1 rounded">{t('calendar.cancelled')}</span>
                      )}
                      {app.status === 'clinic_requested' && (
                        <span className="ml-2 text-xs bg-purple-500 text-white px-1 rounded">{t('calendar.clinicRequest')}</span>
                      )}
                    </div>
                    <div className="text-gray-500">{app.doctor_name || doctors.find(d => d.id === app.doctor_id)?.name}</div>
                  </div>
                ))}
                {displayedAppointments.length > 4 && (
                  <div className="border rounded p-2 text-xs bg-gray-50 flex items-center justify-center hover:bg-gray-100 hover:border-blue-300 transition-all duration-150 cursor-pointer">
                    {t('calendar.moreAppointments').replace('...', ` ${displayedAppointments.length - 4} ...`)}
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="min-w-max">
            {/* Table header */}
            <div className="flex border-b">
              <div className="w-20 min-w-20 p-2 bg-gray-200 border-r text-center text-xs font-medium">

              </div>
              {doctors.map(doctor => (
                <div key={doctor.id} className="w-40 min-w-40 p-2 bg-gray-200 border-r text-center text-xs font-medium">
                  {doctor.name}
                </div>
              ))}
            </div>

            {/* Time slots */}
            {loading ? (
              <div className="flex justify-center my-12">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-gray-900"></div>
              </div>
            ) : (
              <>
                <div className="max-h-[calc(100vh-220px)] overflow-y-auto">
                  {timeSlots.map((time, index) => (
                    <div key={time} className="flex border-b hover:bg-gray-50">
                      <div className="w-20 min-w-20 p-1 border-r text-center text-xs flex items-center justify-center h-8">
                        {time}
                      </div>
                      {doctors.map(doctor => {
                        // Check if there are appointments for this cell
                        const hasAppointments = displayedAppointments.some(app =>
                          app.doctor_id === doctor.id &&
                          (app.appointment_start_time === time ||
                            (time === '17:45' && (
                              parseInt(app.appointment_start_time.split(':')[0]) < 8 ||
                              parseInt(app.appointment_start_time.split(':')[0]) >= 18
                            ))
                          )
                        );

                        // Check if this time slot is blocked
                        const blockedInfo = isTimeSlotBlocked(time, doctor.id);
                        const isBlocked = blockedInfo.isBlocked;
                        const isLoadingBlockedSchedules = blockedSchedulesLoading;
                        // No schedule for this date => every timeslot unavailable
                        const hasScheduleToday = hasDoctorScheduleForDate(doctor.id);
                        const inSchedule = hasScheduleToday && isTimeSlotInDoctorSchedule(time, doctor.id);
                        const isUnavailable = !hasAppointments && (!hasScheduleToday || !inSchedule || isBlocked);

                        // Determine the cell styling based on availability and blocking
                        let cellClassName = 'w-40 min-w-40 p-1 border-r transition-colors duration-150 ';

                        if (hasAppointments) {
                          cellClassName += 'bg-white';
                        } else if (isLoadingBlockedSchedules || scheduleLoading || !schedulesReady) {
                          cellClassName += 'bg-yellow-50 hover:bg-yellow-100 cursor-wait';
                        } else if (!hasScheduleToday) {
                          // Entire day has no schedule — all slots grey / not bookable
                          cellClassName += 'bg-gray-200 cursor-not-allowed pointer-events-none';
                        } else if (isBlocked) {
                          cellClassName += 'bg-gray-100 hover:bg-red-200 cursor-not-allowed pointer-events-none';
                        } else if (inSchedule) {
                          cellClassName += 'bg-white hover:bg-blue-50 cursor-pointer';
                        } else {
                          // Outside working hours — grey, not clickable
                          cellClassName += 'bg-gray-200 cursor-not-allowed pointer-events-none';
                        }

                        return (
                          <div
                            key={`${doctor.id}-${time}`}
                            className={cellClassName}
                            onClick={
                              !hasScheduleToday || isBlocked || isUnavailable || !schedulesReady || !inSchedule
                                ? undefined
                                : () => handleCellClick(time, doctor.id)
                            }
                            title={isBlocked ? `Blocked: ${blockedInfo.reason}` : undefined}
                          >
                            {renderAppointmentCell(time, doctor.id)}
                          </div>
                        );
                      })}
                    </div>
                  ))}
                </div>

                {/* Legend for available/unavailable/blocked time slots */}
                <div className="flex p-2 bg-gray-100 border-t border-gray-300 text-xs text-gray-600 items-center flex-wrap gap-4">
                  <div className="flex items-center">
                    <div className="w-4 h-4 bg-white border mr-1"></div>
                    <span>{t('calendar.availableSlots')}</span>
                  </div>
                  <div className="flex items-center">
                    <div className="w-4 h-4 bg-gray-200 border mr-1"></div>
                    <span>{t('calendar.unavailableSlots')}</span>
                  </div>
                  <div className="flex items-center">
                    <div className="w-4 h-4 bg-red-100 border mr-1"></div>
                    <span>{t('calendar.blockedSlots')}</span>
                  </div>
                  <div className="flex items-center">
                    <span className="ml-2 text-yellow-500 mr-1">⚠️</span>
                    <span>
                      {t('calendar.outOfHoursWarning')}
                    </span>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Schedule management dialog */}
      <ScheduleDialog
        isOpen={showScheduleForm}
        onClose={handleCloseScheduleForm}
        editingSchedule={editingSchedule}
        scheduleForm={newSchedule}
        onScheduleChange={handleScheduleChange}
        onSubmit={handleScheduleSubmit}
        doctors={doctors}
        isSaving={actionLoading}
      />

      {/* Block Schedule management dialog */}
      <BlockScheduleDialog
        isOpen={showBlockScheduleForm}
        onClose={handleCloseBlockScheduleForm}
        editingBlockSchedule={editingBlockSchedule}
        blockScheduleForm={newBlockSchedule}
        onBlockScheduleChange={handleBlockScheduleChange}
        onSubmit={handleBlockScheduleSubmit}
        doctors={doctors}
        isSaving={actionLoading}
      />

      {/* Schedule Manager Modal */}
      {showScheduleManager && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="flex max-h-[90vh] w-full max-w-5xl flex-col overflow-hidden rounded-xl bg-white shadow-xl">
            <div className="flex items-center justify-between border-b border-gray-100 px-6 py-4">
              <h3 className="text-xl font-semibold text-gray-900">
                {t('calendar.doctorScheduleManagement')}
              </h3>
              <button
                type="button"
                onClick={() => setShowScheduleManager(false)}
                className="-m-2 rounded-lg p-2 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500"
                aria-label={t('calendar.close')}
              >
                ✕
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-6 py-5">
              <DoctorSchedulesJsonPanel
                onImported={() => {
                  if (selectedDoctorForSchedules) {
                    fetchAllSchedulesForDoctor(selectedDoctorForSchedules.id);
                  }
                }}
              />

              <div className="mb-2 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
                <div className="min-w-0 flex-1">
                  <label className="mb-1.5 block text-sm font-medium text-gray-700">
                    {t('calendar.selectDoctor')}
                  </label>
                  <select
                    value={selectedDoctorForSchedules?.id || ''}
                    onChange={handleScheduleManagerDoctorChange}
                    className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-gray-900 transition-shadow focus:border-transparent focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">{t('calendar.pleaseSelectDoctor')}</option>
                    {doctors.map((doctor) => (
                      <option key={doctor.id} value={doctor.id}>
                        {doctor.name}
                      </option>
                    ))}
                  </select>
                </div>
                <button
                  type="button"
                  onClick={handleAddScheduleFromManager}
                  disabled={!selectedDoctorForSchedules}
                  className="inline-flex items-center justify-center rounded-lg bg-neutral-900 px-4 py-2.5 text-sm font-medium text-white shadow-sm transition-colors hover:bg-neutral-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <PlusIcon className="mr-1.5 h-4 w-4" />
                  {t('calendar.addSchedule')}
                </button>
              </div>

              {selectedDoctorForSchedules ? (
                <ScheduleList
                  schedules={allSchedules}
                  onEditSchedule={handleEditSchedule}
                  onDeleteSchedule={handleDeleteSchedule}
                  isLoading={scheduleLoading}
                />
              ) : (
                <div className="mt-4 rounded-xl border border-dashed border-gray-200 bg-gray-50 px-4 py-10 text-center text-sm text-gray-500">
                  {t('calendar.pleaseSelectDoctor')}
                </div>
              )}
            </div>

            <div className="flex justify-end border-t border-gray-100 px-6 py-4">
              <button
                type="button"
                onClick={() => setShowScheduleManager(false)}
                className="rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
              >
                {t('calendar.close')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Block Schedule Manager Modal */}
      {showBlockScheduleManager && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-semibold">{t('calendar.blockScheduleManagement')}</h3>
              <button
                onClick={() => setShowBlockScheduleManager(false)}
                className="text-gray-500 hover:text-gray-700 hover:bg-gray-100 p-1 rounded-full transition-colors duration-150"
              >
                ✕
              </button>
            </div>

            <div className="mb-6">
              <div className="flex justify-end items-center mb-4">
                <button
                  onClick={handleAddBlockScheduleFromManager}
                  className="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600 flex items-center"
                >
                  <PlusIcon className="h-4 w-4 mr-1" />
                  {t('calendar.addBlockSchedule')}
                </button>
              </div>

              <BlockScheduleList
                blockSchedules={allBlockSchedules}
                onEditBlockSchedule={handleEditBlockSchedule}
                onDeleteBlockSchedule={handleDeleteBlockSchedule}
                isLoading={scheduleLoading}
              />
            </div>

            <div className="flex justify-end">
              <button
                onClick={() => setShowBlockScheduleManager(false)}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
              >
                {t('calendar.close')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Appointment creation modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-semibold">{editingAppointment ? t('calendar.editAppointment') : t('calendar.newAppointment')}</h3>
              <button
                type="button"
                onClick={() => {
                  if (editingAppointment) {
                    setEditingAppointment(null);
                  }
                  setShowForm(false);
                  setNewAppointment({
                    customer_name: '',
                    appointment_date: selectedDate.toISOString().split('T')[0],
                    appointment_start_time: '',
                    duration_minutes: 30,
                    doctor_id: doctors[0].id,
                    doctor_name: doctors[0].name,
                    remarks: ''
                  });
                }}
                disabled={actionLoading}
                className={`px-4 py-2 border border-gray-300 text-gray-700 rounded-md transition-colors duration-150 ${actionLoading ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-50'
                  }`}
              >
                {t('calendar.cancel')}
              </button>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('calendar.title')}
                </label>
                <input
                  type="text"
                  name="customer_name"
                  value={newAppointment.customer_name}
                  onChange={handleInputChange}
                  className="w-full p-2 border rounded-md"
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('calendar.date')}
                  </label>
                  <input
                    type="date"
                    name="appointment_date"
                    value={newAppointment.appointment_date}
                    onChange={handleInputChange}
                    className="w-full p-2 border rounded-md"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('calendar.time')}
                  </label>
                  <input
                    type="time"
                    name="appointment_start_time"
                    value={newAppointment.appointment_start_time}
                    onChange={handleInputChange}
                    className="w-full p-2 border rounded-md"
                    required
                  />
                </div>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('calendar.doctor')}
                </label>
                <select
                  name="doctor_id"
                  value={newAppointment.doctor_id}
                  onChange={handleInputChange}
                  className="w-full p-2 border rounded-md"
                  required
                >
                  {doctors.map(doctor => (
                    <option key={doctor.id} value={doctor.id}>
                      {doctor.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('calendar.duration')}
                </label>
                <select
                  name="duration_minutes"
                  value={newAppointment.duration_minutes}
                  onChange={handleInputChange}
                  className="w-full p-2 border rounded-md"
                  required
                >
                  <option value="15">15</option>
                  <option value="30">30</option>
                  <option value="45">45</option>
                  <option value="60">60</option>
                  <option value="90">90</option>
                  <option value="120">120</option>
                </select>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('calendar.remarks')}
                </label>
                <textarea
                  name="remarks"
                  value={newAppointment.remarks}
                  onChange={handleInputChange}
                  className="w-full p-2 border rounded-md"
                  rows="3"
                ></textarea>
              </div>

              <div className="flex justify-end space-x-2 mt-4">
                <button
                  type="button"
                  onClick={() => {
                    if (editingAppointment) {
                      setEditingAppointment(null);
                    }
                    setShowForm(false);
                    setNewAppointment({
                      customer_name: '',
                      appointment_date: selectedDate.toISOString().split('T')[0],
                      appointment_start_time: '',
                      duration_minutes: 30,
                      doctor_id: doctors[0].id,
                      doctor_name: doctors[0].name,
                      remarks: ''
                    });
                  }}
                  disabled={actionLoading}
                  className={`px-4 py-2 border border-gray-300 text-gray-700 rounded-md transition-colors duration-150 ${actionLoading ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-50'
                    }`}
                >
                  {t('calendar.cancel')}
                </button>
                <button
                  type="submit"
                  disabled={actionLoading}
                  className={`px-4 py-2 bg-blue-500 text-white rounded-md transition-colors duration-150 ${actionLoading ? 'opacity-50 cursor-not-allowed' : 'hover:bg-blue-600'
                    }`}
                >
                  {actionLoading ? t('calendar.processing') : editingAppointment ? t('calendar.updateAppointment') : t('calendar.submitAppointment')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Appointment action dialog */}
      {showConfirmDialog && selectedAppointment && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-semibold">{t('calendar.appointmentActions')}</h3>
              <button
                onClick={() => {
                  setShowConfirmDialog(false);
                  setSelectedAppointment(null);
                }}
                disabled={actionLoading}
                className={`text-gray-500 p-1 rounded-full transition-colors duration-150 ${actionLoading ? 'opacity-50 cursor-not-allowed' : 'hover:text-gray-700 hover:bg-gray-100'
                  }`}
              >
                ✕
              </button>
            </div>

            <div className="mb-6">
              <div className="text-lg font-medium mb-2">{selectedAppointment.customer_name}</div>
              <div className="text-sm text-gray-600">
                {selectedAppointment.contact_number && (
                  <div>
                    {t('calendar.contactPhone')} {
                      (() => {
                        const phone = selectedAppointment.contact_number;
                        // Format for Hong Kong numbers (e.g., +852 XXXX XXXX)
                        if (phone.startsWith('+852') || phone.startsWith('852')) {
                          const formattedNumber = phone.replace(/^\+?852\s?/, '');
                          return (
                            <a href={`tel:${phone}`} className="text-blue-500 hover:underline">
                              +852 {formattedNumber}
                            </a>
                          );
                        }
                        // For other numbers, just add hyperlink
                        return (
                          <a href={`tel:${phone}`} className="text-blue-500 hover:underline">
                            {phone}
                          </a>
                        );
                      })()
                    }
                  </div>
                )}
                <div>{t('calendar.appointmentDate')} {selectedAppointment.appointment_date}</div>
                <div>{t('calendar.appointmentTime')} {selectedAppointment.appointment_start_time}</div>
                <div>{t('calendar.appointmentDoctor')} {selectedAppointment.doctor_name || doctors.find(d => d.id === selectedAppointment.doctor_id)?.name}</div>
                <div>{t('calendar.appointmentStatus')} {
                  selectedAppointment.status === 'requested' ? t('calendar.pendingConfirmation') :
                    selectedAppointment.status === 'confirmed' ? t('calendar.confirmed') :
                      selectedAppointment.status === 'cancelled' ? t('calendar.cancelled') :
                        selectedAppointment.status === 'clinic_requested' ? t('calendar.clinicRequest') : t('calendar.unknown')
                }</div>
                {selectedAppointment.remarks && <div className="mt-2 whitespace-pre-line">{t('calendar.appointmentRemarks')} {selectedAppointment.remarks}</div>}
              </div>
            </div>

            <div className="flex flex-wrap justify-end gap-2">
              {selectedAppointment.status !== 'cancelled' && (
                <button
                  onClick={handleCancelAppointment}
                  disabled={actionLoading}
                  className={`px-4 py-2 border border-red-300 text-red-600 rounded-md transition-colors duration-150 ${actionLoading ? 'opacity-50 cursor-not-allowed' : 'hover:bg-red-50'
                    }`}
                >
                  {actionLoading ? t('calendar.processing') : t('calendar.cancelAppointment')}
                </button>
              )}
              {(selectedAppointment.status === 'requested' || selectedAppointment.status === 'clinic_requested' || selectedAppointment.status === 'cancelled') && (
                <>
                  <button
                    onClick={handleConfirmAppointment}
                    disabled={actionLoading}
                    className={`px-4 py-2 bg-green-500 text-white rounded-md transition-colors duration-150 ${actionLoading ? 'opacity-50 cursor-not-allowed' : 'hover:bg-green-600'
                      }`}
                  >
                    {actionLoading ? t('calendar.processing') : t('calendar.confirmAppointment')}
                  </button>
                  <button
                    onClick={handleEditAppointment}
                    disabled={actionLoading}
                    className={`px-4 py-2 bg-blue-500 text-white rounded-md transition-colors duration-150 ${actionLoading ? 'opacity-50 cursor-not-allowed' : 'hover:bg-blue-600'
                      }`}
                  >
                    {actionLoading ? t('calendar.processing') : t('calendar.editAndConfirm')}
                  </button>
                </>
              )}
              {selectedAppointment.status === 'confirmed' && (
                <button
                  onClick={handleEditAppointment}
                  disabled={actionLoading}
                  className={`px-4 py-2 bg-blue-500 text-white rounded-md transition-colors duration-150 ${actionLoading ? 'opacity-50 cursor-not-allowed' : 'hover:bg-blue-600'
                    }`}
                >
                  {actionLoading ? t('calendar.processing') : t('calendar.editAppointment')}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Calendar;
